import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();
const DEFAULT_THEME = 'dark';
const THEME_CHOICE_KEY = 'site-theme-user-choice';

export const ThemeProvider = ({ children }) => {
    // Check explicit localStorage preference or default to dark.
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('site-theme');
        const hasExplicitThemeChoice = localStorage.getItem(THEME_CHOICE_KEY) === 'true';
        return hasExplicitThemeChoice && (savedTheme === 'dark' || savedTheme === 'light')
            ? savedTheme
            : DEFAULT_THEME;
    });

    // Update theme class on document and save to localStorage when theme changes
    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('light-theme', 'dark-theme');
        root.classList.add(`${theme}-theme`);

        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }

        localStorage.setItem('site-theme', theme);
    }, [theme]);

    // Toggle theme function
    const toggleTheme = () => {
        localStorage.setItem(THEME_CHOICE_KEY, 'true');
        setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
    };

    // Set specific theme
    const setThemeMode = (mode) => {
        if (mode === 'light' || mode === 'dark') {
            localStorage.setItem(THEME_CHOICE_KEY, 'true');
            setTheme(mode);
        }
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setThemeMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

// Custom hook to use the theme context
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext; 